// Remove unused import to fix ESLint error
import { getSocket } from 'src/CrudManager/Core/Utils/echoSocket'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useNotificationStore } from '../stores/useNotificationStore'
import type { EchoGroupActionEvent, EchoNotificationEvent, EchoProgressUpdateEvent } from '../types'

export function useNotificationRealTime() {
  const notificationStore = useNotificationStore()
  const userStore = useUserStore()
  const isConnected = ref(false)
  const connectionError = ref<string | null>(null)

  interface EchoChannel {
    notification: (callback: (event: EchoNotificationEvent) => void) => void
    listen: (event: string, callback: (data: unknown) => void) => void
    stopListening: (event: string) => void
  }

  let userChannel: EchoChannel | null = null

  const connect = async () => {
    try {
      if (!userStore.user?.id) {
        console.warn('Cannot connect to real-time notifications: No user ID')
        return
      }

      const echoSocket = getSocket()
      if (!echoSocket) {
        console.warn('Cannot connect to real-time notifications: No socket available')
        connectionError.value = 'WebSocket connection not available'
        return
      }

      // Listen to user-specific notifications
      const userChannel = echoSocket.private(`notification.user.${userStore.user.id}`) as EchoChannel

      // Handle new notifications

      userChannel.listen('.notification.created', (event: unknown) => {
        console.log('Received notification event:', event)
        notificationStore.handleEchoNotificationEvent(event as EchoNotificationEvent)
      })

      // Handle notification read/unread events (these might not be broadcast events, but handled via API)
      // userChannel.listen('NotificationRead', (event: unknown) => {
      //   console.log('Received notification read event:', event)
      //   notificationStore.handleEchoNotificationEvent(event as EchoNotificationEvent)
      // })

      // Handle progressive notification updates on the user channel
      userChannel.listen('.notification.progress.updated', (event: unknown) => {
        console.log('Received progress update:', event)
        notificationStore.handleEchoProgressUpdate(event as EchoProgressUpdateEvent)
      })

      // Handle group actions on the user channel (user receives when they're part of the group)
      userChannel.listen('.notification.group.completed', (event: unknown) => {
        console.log('Received group action event:', event)
        notificationStore.handleEchoGroupAction(event as EchoGroupActionEvent)
      })

      // Connection status tracking
      echoSocket.connector.pusher.connection.bind('connected', () => {
        console.log('Real-time notifications connected')
        isConnected.value = true
        connectionError.value = null
        notificationStore.setRealTimeEnabled(true)
      })

      echoSocket.connector.pusher.connection.bind('disconnected', () => {
        console.log('Real-time notifications disconnected')
        isConnected.value = false
        notificationStore.setRealTimeEnabled(false)
      })

      echoSocket.connector.pusher.connection.bind('error', (error: Error) => {
        console.error('Real-time notifications error:', error)
        connectionError.value = error.message
        isConnected.value = false
        notificationStore.setRealTimeEnabled(false)
      })

      // Handle authentication failures
      echoSocket.connector.pusher.connection.bind('failed', () => {
        console.error('Real-time notifications authentication failed')
        connectionError.value = 'Authentication failed'
        isConnected.value = false
        notificationStore.setRealTimeEnabled(false)
      })
    } catch (error) {
      console.error('Failed to connect to real-time notifications:', error)
      connectionError.value = error instanceof Error ? error.message : 'Connection failed'
      isConnected.value = false
      notificationStore.setRealTimeEnabled(false)
    }
  }

  const disconnect = () => {
    try {
      if (userChannel) {
        userChannel.stopListening('.notification.created')
        userChannel.stopListening('.notification.progress.updated')
        userChannel.stopListening('.notification.group.completed')
        userChannel = null
      }

      // All events now handled through user channel for proper authorization

      isConnected.value = false
      notificationStore.setRealTimeEnabled(false)
      console.log('Real-time notifications disconnected')
    } catch (error) {
      console.error('Error disconnecting real-time notifications:', error)
    }
  }

  const reconnect = async () => {
    disconnect()
    await new Promise((resolve) => setTimeout(resolve, 1000)) // Wait 1 second before reconnecting
    await connect()
  }

  const subscribeToGroup = (groupId: string): EchoChannel | null => {
    try {
      const echoSocket = getSocket()
      if (!echoSocket) {
        console.warn('Cannot subscribe to group: No socket available')
        return null
      }

      const groupPrivateChannel = echoSocket.private(`notification.group.${groupId}`)
      const groupChannel = groupPrivateChannel as EchoChannel

      groupChannel.listen('.notification.group.completed', (event: unknown) => {
        console.log('Received group completion for group:', groupId, event)
        notificationStore.handleEchoGroupAction(event as EchoGroupActionEvent)
      })

      return groupChannel
    } catch (error) {
      console.error('Failed to subscribe to group notifications:', error)
      return null
    }
  }

  const unsubscribeFromGroup = (groupChannel: ReturnType<typeof subscribeToGroup>) => {
    try {
      if (groupChannel) {
        groupChannel.stopListening('.notification.group.completed')
      }
    } catch (error) {
      console.error('Error unsubscribing from group notifications:', error)
    }
  }

  const subscribeToProgressiveNotification = (notificationId: string): EchoChannel | null => {
    try {
      const echoSocket = getSocket()
      if (!echoSocket) {
        console.warn('Cannot subscribe to progressive notification: No socket available')
        return null
      }

      // Progressive notifications are broadcast to user/group channels, not individual notification channels
      // So we don't need a separate channel subscription here
      // The progress updates will be received through the main user channel subscription above
      console.log(`Progressive notification subscription for ${notificationId} not needed - using main user channel`)
      return null
    } catch (error) {
      console.error('Failed to subscribe to progressive notification:', error)
      return null
    }
  }

  const unsubscribeFromProgressiveNotification = (_progressiveChannel: ReturnType<typeof subscribeToProgressiveNotification>) => {
    // Since we don't actually subscribe to progressive channels anymore,
    // this function is a no-op but kept for API compatibility
    console.log('Progressive notification unsubscription not needed - using main user channel')
  }

  // Auto-connect on mount
  onMounted(() => {
    if (userStore.user?.id) {
      connect()
    }
  })

  // Cleanup on unmount
  onBeforeUnmount(() => {
    disconnect()
  })

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    reconnect,
    subscribeToGroup,
    unsubscribeFromGroup,
    subscribeToProgressiveNotification,
    unsubscribeFromProgressiveNotification,
  }
}
